import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// third-party
import { useTranslation } from 'react-i18next';

// project imports
import MainCard from 'components/MainCard';
import navigation from 'menu-items';
import { ThemeDirection } from 'config';

// assets
import CorporateFareIcon from '@mui/icons-material/CorporateFare'; // Replaces CorporateFareIcon (or ApartmentIcon)
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'; // Replaces HomeOutlinedIcon
import HomeIcon from '@mui/icons-material/Home'; // Replaces HomeIcon


// ─────────────────────────────────────────────────────────────
// Utility: recursively search subRoutes and build a crumb trail
// Returns array of { url, title } in order, or null if no match
//
// Example result for '/bulk-customer/report':
//   [
//     { url: '/bulk-customer',        title: 'Bulk Customer' },
//     { url: '/bulk-customer/report', title: 'Report'        }
//   ]
// ─────────────────────────────────────────────────────────────
function matchSubRoute(subRoutes, currentPath, trail = []) {
  for (const sub of subRoutes) {
    const currentTrail = [...trail, { url: sub.url, title: sub.title }];

    // Exact match
    if (sub.url === currentPath) {
      return currentTrail;
    }

    // Recurse into deeper subRoutes
    if (sub.subRoutes?.length) {
      const deepMatch = matchSubRoute(sub.subRoutes, currentPath, currentTrail);
      if (deepMatch) return deepMatch;
    }
  }

  return null;
}

// ─────────────────────────────────────────────────────────────
// Main Breadcrumbs Component
// ─────────────────────────────────────────────────────────────
export default function Breadcrumbs({
  card = false,
  custom = false,
  divider = false,
  heading,
  icon,
  icons,
  links,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}) {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();

  const [main, setMain] = useState();   // parent collapse  e.g. "Customer"
  const [item, setItem] = useState();   // sidebar item     e.g. "Create"
  const [subTrail, setSubTrail] = useState([]); // nested sub crumbs e.g. [{Bulk}, {Report}]

  const iconSX = {
    marginRight: theme.direction === ThemeDirection.RTL ? 0 : theme.spacing(0.75),
    marginLeft: theme.direction === ThemeDirection.RTL ? theme.spacing(0.75) : 0,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  let customLocation = location.pathname;

  // only used for component demo breadcrumbs
  if (customLocation.includes('/components-overview/breadcrumbs')) {
    customLocation = '/apps/customer/customer-card';
  }

  // ── Recursive collapse walker ──────────────────────────────
  const getCollapse = (menu) => {
    if (!custom && menu.children) {
      menu.children.forEach((collapse) => {

        if (collapse.type === 'collapse') {
          getCollapse(collapse);
          if (collapse.url === customLocation) {
            setMain(collapse);
            setItem(collapse);
          }

        } else if (collapse.type === 'item') {

          // 1. Direct URL match → normal breadcrumb
          if (customLocation === collapse.url) {
            setMain(menu);
            setItem(collapse);
            setSubTrail([]);
          }

          // 2. Check subRoutes (supports infinite nesting via recursion)
          if (collapse.subRoutes?.length) {
            const trail = matchSubRoute(collapse.subRoutes, customLocation);
            if (trail) {
              setMain(menu);        // grandparent  e.g. "Customer"
              setItem(collapse);    // parent item  e.g. "Create"
              setSubTrail(trail);   // sub crumbs   e.g. [{Bulk Customer}, {Report}]
            }
          }
        }
      });
    }
  };

  // ── Effect: run on every path change ──────────────────────
  useEffect(() => {
    // Reset all state first
    setMain(undefined);
    setItem(undefined);
    setSubTrail([]);

    navigation?.items?.forEach((menu) => {
      if (menu.type && menu.type === 'group') {
        if (menu?.url && menu.url === customLocation) {
          setMain(menu);
          setItem(menu);
        } else {
          getCollapse(menu);
        }
      }
    });
  }, [customLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Separator ──────────────────────────────────────────────
  const SeparatorIcon = separator;
  const separatorIcon = separator
    ? <SeparatorIcon style={{ fontSize: '0.75rem', marginTop: 2 }} />
    : '/';

  // ── Build crumb pieces ─────────────────────────────────────
  let mainContent;
  let itemContent;
  let breadcrumbContent = <Typography />;
  let itemTitle = '';
  let CollapseIcon;
  let ItemIcon;

  // ── Collapse (parent group) crumb ──────────────────────────
  // In Breadcrumbs.jsx — update mainContent

  if (main && main.type === 'collapse' && !main.breadcrumbs) {
    CollapseIcon = main.icon ? main.icon : CorporateFareIcon;

    // Get first child that has a url
    const firstChildUrl = main.children?.find(child => child.url)?.url;

    mainContent = (
      <Typography
        {...(firstChildUrl && { component: Link, to: firstChildUrl })} // ← goes to first child
        variant="h6"
        sx={{ textDecoration: 'none' }}
        color="text.secondary"
      >
        {icons && <CollapseIcon style={iconSX} />}
        {t(main?.title)}
      </Typography>
    );

    if (!!custom) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={card === false ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx } : { mb: 3, ...sx }}
          {...others}
          content={card}
          shadow="none"
        >
          <Grid
            container
            direction={rightAlign ? 'row' : 'column'}
            justifyContent={rightAlign ? 'space-between' : 'flex-start'}
            alignItems={rightAlign ? 'center' : 'flex-start'}
            spacing={1}
          >
            <Grid>
              <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
                <Typography component={Link} to="/dashboard" color="text.secondary" variant="h6" sx={{ textDecoration: 'none' }}>
                  {icons && <HomeOutlinedIcon style={iconSX} />}
                  {icon && !icons && <HomeIcon style={{ ...iconSX, marginRight: 0 }} />}
                  {(!icon || icons) && t('home')}
                </Typography>
                {mainContent}
              </MuiBreadcrumbs>
            </Grid>
          </Grid>
          {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
        </MainCard>
      );
    }
  }

  // ── Item crumb (+ subTrail) ────────────────────────────────
  if ((item && item.type === 'item') || (item?.type === 'group' && item?.url) || custom) {
    itemTitle = item?.title;

    ItemIcon = item?.icon ? item.icon : CorporateFareIcon;

    // If subTrail has entries, the sidebar item becomes a clickable link
    const hasSubTrail = subTrail.length > 0;

    itemContent = (
      <Typography
        {...(hasSubTrail && item?.url ? { component: Link, to: item.url } : {})}
        variant={hasSubTrail ? 'h6' : 'subtitle1'}
        color={hasSubTrail ? 'text.secondary' : 'text.primary'}
        sx={{ textDecoration: 'none' }}
      >
        {icons && <ItemIcon style={iconSX} />}
        {t(itemTitle)}
      </Typography>
    );

    // Build sub-trail crumbs — every crumb except the last is a link
    const subTrailContent = subTrail.map((crumb, index) => {
      const isLastCrumb = index === subTrail.length - 1;
      return (
        <Typography
          key={crumb.url}
          {...(!isLastCrumb && crumb.url ? { component: Link, to: crumb.url } : {})}
          variant={isLastCrumb ? 'subtitle1' : 'h6'}
          color={isLastCrumb ? 'text.primary' : 'text.secondary'}
          sx={{ textDecoration: 'none' }}
        >
          {t(crumb.title)}
        </Typography>
      );
    });

    // Default breadcrumb trail
    let tempContent = (
      <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
        {/* Home */}
        <Typography component={Link} to="/dashboard" color="text.secondary" variant="h6" sx={{ textDecoration: 'none' }}>
          {icons && <HomeOutlinedIcon style={iconSX} />}
          {icon && !icons && <HomeIcon style={{ ...iconSX, marginRight: 0 }} />}
          {(!icon || icons) && t('home')}
        </Typography>

        {/* Parent collapse  e.g. "Customer" */}
        {mainContent}

        {/* Sidebar item    e.g. "Create"   (link if subTrail exists) */}
        {itemContent}

        {/* Sub-route trail e.g. "Bulk Customer" → "Report" */}
        {subTrailContent}
      </MuiBreadcrumbs>
    );

    // Custom breadcrumb with manual links prop
    if (custom && links && links?.length > 0) {
      tempContent = (
        <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
          {links?.map((link, index) => {
            CollapseIcon = link.icon ? link.icon : CorporateFareIcon;
            return (
              <Typography
                key={index}
                {...(link.to && { component: Link, to: link.to })}
                variant={!link.to ? 'subtitle1' : 'h6'}
                sx={{ textDecoration: 'none' }}
                color={!link.to ? 'text.primary' : 'text.secondary'}
              >
                {link.icon && <CollapseIcon style={iconSX} />}
                {t(link.title)}
              </Typography>
            );
          })}
        </MuiBreadcrumbs>
      );
    }

    if (item?.breadcrumbs !== false || custom) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={card === false ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx } : { mb: 3, ...sx }}
          {...others}
          content={card}
          shadow="none"
        >
          <Grid
            container
            direction={rightAlign ? 'row' : 'column'}
            justifyContent={rightAlign ? 'space-between' : 'flex-start'}
            alignItems={rightAlign ? 'center' : 'flex-start'}
            spacing={1}
          >
            <Grid>{tempContent}</Grid>
          </Grid>
          {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
        </MainCard>
      );
    }
  }

  return breadcrumbContent;
}

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  custom: PropTypes.bool,
  divider: PropTypes.bool,
  heading: PropTypes.string,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  links: PropTypes.array,
  maxItems: PropTypes.number,
  rightAlign: PropTypes.bool,
  separator: PropTypes.any,
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  sx: PropTypes.any,
  others: PropTypes.any
};